import { createRouter, createWebHistory } from 'vue-router'
import { useAuth } from '@shared/composables/useAuth.js'

const NoAccessView = {
  template: `
    <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui">
      <div style="text-align:center;max-width:400px;padding:2rem">
        <h2 style="margin:0 0 0.5rem;color:#1a1a1a">Module Not Available</h2>
        <p style="color:#666;margin:0 0 1.5rem">Your account does not have access to the Library module. Please contact your administrator.</p>
        <button onclick="window.history.back()" style="padding:8px 20px;border:1px solid #d1d5db;border-radius:6px;background:#fff;cursor:pointer;font-size:14px">Go Back</button>
      </div>
    </div>
  `,
}

const routes = [
  {
    path: '/no-access',
    name: 'no-access',
    component: NoAccessView,
    meta: { requiresAuth: false },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    name: 'dashboard',
    component: () => import('../views/DashboardView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/books',
    name: 'books',
    component: () => import('../views/BooksView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/books/:id',
    name: 'book-detail',
    component: () => import('../views/BookDetailView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/members',
    name: 'members',
    component: () => import('../views/MembersView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/members/:id',
    name: 'member-detail',
    component: () => import('../views/MemberDetailView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/circulation',
    name: 'circulation',
    component: () => import('../views/CirculationView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reservations',
    name: 'reservations',
    component: () => import('../views/ReservationsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/fines',
    name: 'fines',
    component: () => import('../views/FinesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/no-dues',
    name: 'no-dues',
    component: () => import('../views/NoDuesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/subjects',
    name: 'subjects',
    component: () => import('../views/SubjectsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/borrowing-rules',
    name: 'borrowing-rules',
    component: () => import('../views/BorrowingRulesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/footfall',
    name: 'footfall',
    component: () => import('../views/FootfallView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('../views/SettingsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/naac',
    name: 'naac-dashboard',
    component: () => import('../views/NAACDashboardView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/accession-register',
    name: 'accession-register',
    component: () => import('../views/AccessionRegisterView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/stock-verification',
    name: 'stock-verification',
    component: () => import('../views/StockVerificationView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/committee-meetings',
    name: 'committee-meetings',
    component: () => import('../views/CommitteeMeetingsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports',
    name: 'reports',
    component: () => import('../views/ReportsView.vue'),
    meta: { requiresAuth: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Navigation guard - check authentication and module access
router.beforeEach(async (to, from, next) => {
  const { isAuthenticated, fetchMe, hasModuleAccess, user } = useAuth()
  const { apiClient } = await import('@shared/utils/apiClient.js')

  // Route requires auth
  if (to.meta.requiresAuth) {
    // Check if token exists
    if (!apiClient.isAuthenticated()) {
      // No token - redirect to login
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }

    // Token exists, fetch user info if not loaded
    if (!user.value) {
      try {
        await fetchMe()
      } catch (error) {
        // Failed to fetch user (token invalid) - redirect to login
        next({ name: 'login', query: { redirect: to.fullPath } })
        return
      }
    }

    // Check library module access
    if (!hasModuleAccess('library')) {
      next({ name: 'no-access' })
      return
    }
  }

  // Already logged in and trying to access login page
  if (to.name === 'login' && isAuthenticated.value) {
    next({ name: 'dashboard' })
    return
  }

  next()
})

export default router
